/**
 * Workspace product areas (contracts `FeatureArea`): which ones an owner or admin has switched off, what that hides,
 * and which API paths it blocks. Pure, shared by the API (enforcement, validation) and every client (the rail, the
 * sidebar, search, notifications, links and the Settings → Features screen).
 *
 * Like Google Workspace's per-service switches and Slack's admin settings: off hides the area and blocks its use;
 * nothing is deleted, and turning it back on brings everything back. A missing key means on, so old workspaces and
 * areas added later stay enabled. `calendar` and `contacts` are areas of their own (their own views, on by default,
 * with Mail off too); so is `huddles` (calls, meeting links and huddle notes work with Chat off; a conversation's huddle still needs Chat).
 */
import type { FeatureAdminAccess, FeatureArea, PageAccess, WorkspaceFeatures } from "@bundle/contracts";
import { isManager } from "./roles.ts";
import type { Route, ShellView } from "./shell.ts";
import type { WorkspaceCache } from "./workspace.ts";

/** Every area, in the order Settings lists them. */
export const FEATURE_AREAS: readonly FeatureArea[] = ["chat", "mail", "calendar", "contacts", "docs", "huddles"];

/** The area an area lives inside (off ⇒ the child is off too, whatever its own switch says). */
export const FEATURE_PARENT: Readonly<Partial<Record<FeatureArea, FeatureArea>>> = {};

/** Whether `area` is usable: its own switch and its parent's are on (absent ⇒ on). */
export function isFeatureEnabled(features: WorkspaceFeatures | undefined, area: FeatureArea): boolean {
  if (features?.[area] === false) return false;
  const parent = FEATURE_PARENT[area];
  return parent === undefined || isFeatureEnabled(features, parent);
}

/** How much of an area someone gets: everything, look but don't change, or nothing. */
export type FeatureAccess = "full" | "read" | "none";

/**
 * What a person gets of `area`: `full` while it's on; while it's off, owners and admins (`manager`) keep what the
 * workspace chose (`adminAccess`, absent ⇒ none) and everyone else gets nothing.
 */
export function featureAccess(features: WorkspaceFeatures | undefined, area: FeatureArea, manager: boolean, adminAccess?: FeatureAdminAccess): FeatureAccess {
  if (isFeatureEnabled(features, area)) return "full";
  return manager ? adminAccess ?? "none" : "none";
}

/** What the signed-in person gets of `area`: `featureAccess` with their role (active owners and admins are managers). */
export function myFeatureAccess(cache: WorkspaceCache, area: FeatureArea, now: number): FeatureAccess {
  const me = cache.members[cache.meId];
  return featureAccess(cache.workspace.features, area, me !== undefined && isManager(me, now), cache.workspace.featureAdminAccess);
}

/**
 * A page's access as I may use it: at most `view` while Docs is look-only for me (off, owners and admins may still
 * look), so the tree, the editor and databases go read-only like a page shared with view access.
 */
export function pageAccessWith(access: PageAccess, docs: FeatureAccess): PageAccess {
  return docs === "full" ? access : "view";
}

/** Whether a person sees `area` at all (on, or off with admin access kept). */
export function canSeeFeature(features: WorkspaceFeatures | undefined, area: FeatureArea, manager: boolean, adminAccess?: FeatureAdminAccess): boolean {
  return featureAccess(features, area, manager, adminAccess) !== "none";
}

/** The usable areas, in display order. */
export function enabledFeatures(features: WorkspaceFeatures | undefined): FeatureArea[] {
  return FEATURE_AREAS.filter((a) => isFeatureEnabled(features, a));
}

/**
 * The stored form after a change: `patch` merged over `current`, keeping only `false` entries (on is the default)
 * and dropping unknown keys. Returns undefined when everything is on.
 */
export function applyFeatures(current: WorkspaceFeatures | undefined, patch: WorkspaceFeatures): WorkspaceFeatures | undefined {
  const next: WorkspaceFeatures = {};
  for (const a of FEATURE_AREAS) {
    const v = patch[a] ?? current?.[a];
    if (v === false) next[a] = false;
  }
  return Object.keys(next).length === 0 ? undefined : next;
}

/** At least one area must stay usable (an empty workspace has nothing to open). */
export function hasEnabledFeature(features: WorkspaceFeatures | undefined): boolean {
  return enabledFeatures(features).length > 0;
}

/** Whether Settings may switch `area` to `on` (false when it is the last usable area being turned off). */
export function canSetFeature(features: WorkspaceFeatures | undefined, area: FeatureArea, on: boolean): boolean {
  return on || hasEnabledFeature(applyFeatures(features, { [area]: false }));
}

/** The area a route belongs to, or undefined for neutral routes (home, activity, later, settings, agents…). */
export function featureOfRoute(route: Route): FeatureArea | undefined {
  switch (route.type) {
    case "channel":
    case "dm":
    case "thread":
    case "unreads":
    case "scheduled":
    case "drafts":
    // Message search (the omnibox still finds pages and people).
    case "search":
      return "chat";
    case "huddle":
    case "huddles":
      return "huddles";
    case "mail":
    case "mailThread":
      return "mail";
    case "calendar":
      return "calendar";
    case "contacts":
      return "contacts";
    case "page":
    case "db":
    case "docs":
    case "projects":
      return "docs";
    default:
      return undefined;
  }
}

/** Whether a route may open: neutral routes always, area routes while their area is on. */
export function isRouteEnabled(features: WorkspaceFeatures | undefined, route: Route): boolean {
  const area = featureOfRoute(route);
  return area === undefined || isFeatureEnabled(features, area);
}

/** The area that owns an activity-bar view (Files has no switch of its own: always on). */
export const VIEW_FEATURE: Readonly<Partial<Record<ShellView, FeatureArea>>> = { chat: "chat", email: "mail", calendar: "calendar", contacts: "contacts", huddles: "huddles", docs: "docs" };

/** The activity-bar views to show, in order. */
export function enabledViews(features: WorkspaceFeatures | undefined, views: readonly ShellView[]): ShellView[] {
  return views.filter((v) => {
    const area = VIEW_FEATURE[v];
    return area === undefined || isFeatureEnabled(features, area);
  });
}

/**
 * The area a workspace API path (`/v1/w/:ws/<segment>/…`) belongs to, by its first segment after the workspace, or
 * undefined for shared routes (files, settings, search, members…). The API answers 403 `feature_disabled` for these
 * while the area is off. Contacts live under `mail/contacts` but belong to Contacts.
 */
export function featureOfApiPath(path: string): FeatureArea | undefined {
  const m = /^\/v1\/w\/[^/]+\/([^/?]+)(?:\/([^/?]+))?/.exec(path);
  if (m === null) return undefined;
  if (m[1] === "mail" && m[2] === "contacts") return "contacts";
  return API_SEGMENT_FEATURE[m[1]!];
}

const API_SEGMENT_FEATURE: Readonly<Record<string, FeatureArea>> = {
  channels: "chat",
  messages: "chat",
  scheduled: "chat",
  drafts: "chat",
  // Message search (contracts searchMessages); pages and mail are searched through their own routes.
  search: "chat",
  mail: "mail",
  calendar: "calendar",
  pages: "docs",
  databases: "docs",
  rows: "docs",
  "row-templates": "docs",
  templates: "docs",
  huddles: "huddles",
  meetings: "huddles",
};
